import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Menu, Package, FolderOpen, Tag, Users, FileText, ShoppingCart, Star, UserCheck, Warehouse, Archive, Wallet, Truck, Receipt, ClipboardCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSidebar } from '@/contexts/SidebarContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

type NavigationItem = {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  children?: NavigationItem[];
}

const navigation: NavigationItem[] = [
  { name: 'orders', href: '/orders', icon: ShoppingCart },
  { name: 'products', href: '/products', icon: Package, children: [
    { name: 'categories', href: '/categories', icon: FolderOpen },
    { name: 'brands', href: '/brands', icon: Tag },
    { name: 'collections', href: '/collections', icon: Star },
  ]},
  { name: 'customers', href: '/customers', icon: UserCheck },
  { name: 'incoming', href: '/incoming-orders', icon: Truck, badge: 'Building', children: [
    { name: 'incoming-orders', href: '/incoming-orders', icon: Truck, badge: 'Building' },
    { name: 'suppliers', href: '/suppliers', icon: Users, badge: 'Building' },
  ]},
  { name: 'warehouses', href: '/warehouses', icon: Warehouse },
  { name: 'inventory', href: '/inventory', icon: Archive, children: [
    { name: 'stock-count', href: '/inventory/stock-count', icon: ClipboardCheck },
  ]},
  { name: 'wallets', href: '/wallets', icon: Wallet },
  { name: 'expenses', href: '/expenses', icon: Receipt },
  // Removed for MVP - incomplete features
  { name: 'users', href: '/users', icon: Users },
  // { name: 'logs', href: '/logs', icon: FileText }, // Removed for MVP - incomplete feature
]

export const Sidebar: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { isCollapsed, toggle } = useSidebar()


  return (
    <div className={cn('flex flex-col fixed inset-y-0 transition-all', isCollapsed ? 'w-16' : 'w-64')}>
      <div className="flex flex-col flex-grow bg-sidebar border-r border-sidebar-border overflow-y-auto">
        <div className="flex items-center h-16 px-3 border-b border-sidebar-border justify-between">
          {!isCollapsed && (
            <h1 className="text-xl font-bold text-sidebar-foreground">{t('common.appTitle')}</h1>
          )}
          <Button variant="ghost" size="icon" aria-label="Toggle sidebar" onClick={toggle}>
            <span className="sr-only">Toggle sidebar</span>
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="mt-4 flex-1 px-2 space-y-1">
          <TooltipProvider disableHoverableContent>
            {navigation.map((item) => {
              // Active if path starts with the item's href or any child's href
              const childActive = item.children?.some((child) =>
                location.pathname.startsWith(child.href)
              ) ?? false
              const isActive = location.pathname === item.href ||
                location.pathname.startsWith(item.href) ||
                childActive
              const link = (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                  )}
                >
                  <item.icon className={cn('h-5 w-5', isCollapsed ? '' : 'mr-3')} />
                  {!isCollapsed && (
                    <span className="flex w-full items-center justify-between">
                      <span>{t(`nav.${item.name}`)}</span>
                      {item.badge && (
                        <Badge
                          variant="outline"
                          className="ml-2 border-primary/40 bg-primary/5 text-[10px] font-medium uppercase tracking-wide text-primary"
                        >
                          {item.badge}
                        </Badge>
                      )}
                    </span>
                  )}
                </Link>
              )
              const itemNode = isCollapsed ? (
                <Tooltip key={item.name} delayDuration={200}>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right">{t(`nav.${item.name}`)}</TooltipContent>
                </Tooltip>
              ) : (
                <div key={item.name}>
                  {link}
                  {item.children && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.children.map((child) => {
                        const childActive = location.pathname === child.href
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={cn(
                              'group flex items-center justify-between px-3 py-1.5 text-sm rounded-md transition-colors',
                              childActive
                                ? 'bg-sidebar-accent/60 text-sidebar-accent-foreground'
                                : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/30 hover:text-sidebar-accent-foreground'
                            )}
                          >
                            <div className="flex items-center">
                              <child.icon className="mr-2 h-4 w-4" />
                              {t(`nav.${child.name}`)}
                            </div>
                            {child.badge && (
                              <Badge
                                variant="outline"
                                className="ml-2 border-primary/40 bg-primary/5 text-[9px] font-medium uppercase tracking-wide text-primary"
                              >
                                {child.badge}
                              </Badge>
                            )}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
              return itemNode
            })}
          </TooltipProvider>
        </nav>
      </div>
    </div>
  )
}
